import React, { ReactElement, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { nginxParamList } from "constants/webApp";

import IconSelector from "components/shared/images/IconSelector";
import AppModal from "components/shared/modal/AppModal";

import { postEditNginxConfig } from "redux/handlers/webAppHandler";

import { getServerDetails } from "redux/selectors/serversSelector";

import { useAppDispatch } from "hooks/reduxHook";

const EditNginxConf = (): ReactElement => {
 const intl = useIntl();
 const dispatch = useAppDispatch();

 const server = useSelector(getServerDetails);

 const [open, setOpen] = useState<boolean>(false);
 const [loading, setLoading] = useState<boolean>(false);
 const [parameter, setParameter] = useState<string>("");
 const [value, setValue] = useState<string | number>("");
 const [parameterList, setParameterList] = useState<Array<string>>([]);

 const handleOpen = async () => {
  const datas = Object.keys(server.nginx_conf || []);
  setParameterList(datas);
  setOpen(true);
 };
 const handleClose = () => setOpen(false);

 const handleConfirm = async () => {
  setLoading(true);
  await dispatch(postEditNginxConfig(server.id || 0, parameter, value));
  setLoading(false);
  handleClose();
 };

 const RenderField = (): ReactElement => {
  let findedValue = nginxParamList.find((element) => element.param === parameter);

  switch (findedValue?.type) {
   case "open_number":
    return (
     <TextField
      autoComplete="off"
      value={value}
      autoFocus
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
       e.stopPropagation();
      }}
      type="number"
      label={intl.formatMessage({ id: "docker.website.editNginxConfigValue" })}
      InputLabelProps={{ shrink: true }}
      onChange={({ currentTarget }) => setValue(currentTarget.value)}
     />
    );
   default:
    return <></>;
  }
 };

 const formatDefaultValue = (param: string) => {
  if (param === "client_max_body_size") {
   if (server.nginx_conf?.client_max_body_size) {
    setValue(Number(server.nginx_conf?.client_max_body_size.replaceAll("M", "")));
   } else {
    setValue(10);
   }
  } else {
   setValue("");
  }
 };

 return (
  <>
   <Tooltip title={<FormattedMessage id="docker.website.editNginxConfig" />}>
    <IconButton onClick={() => handleOpen()}>
     <IconSelector icon="ServiceIcon" props={{ color: "kxOrange" }} />
    </IconButton>
   </Tooltip>
   <AppModal
    open={open}
    close={handleClose}
    title={intl.formatMessage({ id: "docker.website.editNginxConfig" })}
    handleClose={handleClose}
    handleConfirm={handleConfirm}
    disabled={loading || parameter === "" || value === "" || value === 0}>
    <Stack spacing={2}>
     <Typography variant="subtitle2">
      <FormattedMessage id="docker.website.editNginxConfigMessage" />
     </Typography>
     <FormControl>
      <InputLabel>
       <FormattedMessage id="docker.website.editNginxConfigParameter" />
      </InputLabel>
      <Select
       value={parameter}
       label={<FormattedMessage id="docker.website.editNginxConfigParameter" />}
       onChange={(e) => {
        formatDefaultValue(e.target.value);
        setParameter(e.target.value);
       }}>
       {parameterList.map((element, index) => {
        return (
         <MenuItem key={`parameter-${index}`} value={element}>
          {element}
         </MenuItem>
        );
       })}
      </Select>
     </FormControl>
     <RenderField />
    </Stack>
   </AppModal>
  </>
 );
};

export default EditNginxConf;
